import React, { useContext, useEffect, useMemo, useState } from 'react';
import { db, functions } from '../firebase';
import { AuthContext } from '../Firebase/AuthProvider';
import { openOrderOnWebsite } from '../Funtions/openOrderOnWebsite';
import {
  ALTERATIONS_COLLECTION, DAMAGE_FIXES, DAMAGE_WINDOW_HOURS, FIT_WINDOW_DAYS, FREE_ALTERATIONS, ROUTES,
  allowanceCap, allowanceFor, areasFor, changeText, changeWords, damageWords, directionsFor, hasDamage, isClosed,
  issuesFor, pickupText, reasonsOf, slotLabel, statusText, suggestedPayout, updateMessage, usedFor,
} from '../Data/alterations';

// Care: alteration requests from the website (Alterations/{id}). For each
// piece it shows plainly what the customer said: damage, the fit problems in
// their own words with inches, a booked call, the voice note, the written
// note and the photos with their marks. Approve, move a pickup along its
// steps, choose how damage is put right (clean, repair, remake, refund), pay
// back a local tailor's bill by UPI (up to each garment's limit), refund,
// turn one down, and record the change actually made. Every step can email
// the customer (sendReplyEmail) and there is a ready WhatsApp message.
// Rules: Data/alterations.js, shared with the website; plan: website
// docs/after-order-plan.md.

const FILTERS = [['open', 'Open'], ['requested', 'New'], ['damage', 'Damage'], ['bill_sent', 'Bill to check'], ['done', 'Done'], ['declined', 'Not possible'], ['all', 'All']];
const NEXT = {
  pickup: { requested: 'approved', approved: 'picked_up', picked_up: 'altering', altering: 'shipped_back', shipped_back: 'done' },
  local: { requested: 'approved' },
};
const NEXT_LABEL = {
  approved: 'Approve', picked_up: 'Mark picked up', altering: 'Mark being worked on', shipped_back: 'Mark shipped back', done: 'Mark delivered back',
};

const toDate = (v) => { const r = v?.toDate ? v.toDate() : v; const d = r ? new Date(r) : null; return d && !Number.isNaN(d.getTime()) ? d : null; };
const when = (v) => { const d = toDate(v); return d ? d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''; };
const dayOf = (v) => { const d = toDate(v); return d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''; };
const age = (v) => {
  const d = toDate(v); if (!d) return '';
  const h = Math.floor((Date.now() - d.getTime()) / 3600000);
  return h < 1 ? 'just now' : h < 48 ? `${h} h open` : `${Math.floor(h / 24)} days open`;
};
const gap = (from, to) => {
  const a = toDate(from); const b = toDate(to); if (!a || !b) return '';
  const h = Math.max(0, Math.round((b - a) / 3600000));
  return h < 48 ? `${h} hours` : `${Math.round(h / 24)} days`;
};
const addressText = (a) => {
  if (!a) return '';
  const state = a.state && typeof a.state === 'object' ? a.state.name : a.state;
  return [[a.name, a.last_name].filter(Boolean).join(' '), a.house, a.area || a.locality, a.cityName || a.city, state, a.pincode, a.phone].filter(Boolean).join(', ');
};
const phone10 = (p) => String(p || '').replace(/\D/g, '').slice(-10);

const card = { background: '#fff', border: '1px solid #e2d7c6', borderRadius: 4, padding: 16 };
const input = { height: 34, padding: '0 8px', border: '1px solid #d7cbb7', borderRadius: 3, fontSize: '0.85rem', background: '#fff' };
const hint = { fontSize: '0.72rem', fontWeight: 500, color: '#7a6750' };
const label = { fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#7a6750' };
const btn = (dark, color) => ({
  height: 34, padding: '0 12px', borderRadius: 3, cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem',
  border: dark ? `1px solid ${color || '#3e4a2f'}` : '1px solid #d7cbb7', background: dark ? (color || '#3e4a2f') : '#fff', color: dark ? '#fff' : (color || '#3e4a2f'),
});
const tag = (bg, color = '#fff') => ({ background: bg, color, padding: '2px 8px', fontWeight: 800, fontSize: '0.7rem', borderRadius: 3 });
const pill = (status) => {
  const tone = status === 'declined' ? ['#fdecea', '#9b2c2c'] : isClosed(status) ? ['#e8f4ec', '#187044'] : status === 'requested' || status === 'bill_sent' ? ['#fdf3dc', '#8a5a00'] : ['#e9f0fa', '#1d4f91'];
  return tag(tone[0], tone[1]);
};

// What was actually changed on one garment: rows of area / way / inches.
function FinalChanges({ item, value, onChange }) {
  const areas = areasFor(item.garment);
  const rows = value || [];
  const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <select style={input} value={r.area} onChange={(e) => set(i, { area: e.target.value, direction: '' })}>
            {areas.map((a) => <option key={a}>{a}</option>)}
          </select>
          <select style={input} value={r.direction} onChange={(e) => set(i, { direction: e.target.value })}>
            <option value="">way…</option>
            {directionsFor(r.area).map((d) => <option key={d}>{d}</option>)}
          </select>
          <input style={{ ...input, width: 70 }} type="number" min="0" step="0.25" value={r.inches} onChange={(e) => set(i, { inches: Number(e.target.value) || 0 })} aria-label="inches" />
          <span style={hint}>inch</span>
          <button type="button" style={{ ...btn(false, '#9b2c2c'), height: 30 }} onClick={() => onChange(rows.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      <button type="button" style={{ ...btn(false), alignSelf: 'flex-start', height: 30 }} onClick={() => onChange([...rows, { area: areas[0], direction: '', inches: 0.5 }])}>+ Add a change</button>
    </div>
  );
}

// Photos, with the numbered red marks the customer tapped where it is wrong.
function Photos({ urls, marks = [], big = false }) {
  if (!urls || !urls.length) return null;
  const w = big ? 170 : 72;
  let n = 0;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
      {urls.map((u, i) => (
        <a key={u} href={u} target="_blank" rel="noopener noreferrer" style={{ position: 'relative', display: 'block', width: w, height: w * 1.25 }}>
          <img src={u} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 3, border: '1px solid #e2d7c6' }} />
          {marks.filter((m) => m.photo === i).map((m) => {
            n += 1;
            return (
              <span key={n} style={{ position: 'absolute', left: `${m.x}%`, top: `${m.y}%`, width: 20, height: 20, margin: '-10px 0 0 -10px', borderRadius: '50%', background: '#c0392b', color: '#fff', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 2px #fff' }}>{n}</span>
            );
          })}
        </a>
      ))}
    </div>
  );
}

// One line of what the customer said: Hindi as they saw it, English beside.
const Said = ({ hi, en, tone = '#000', note }) => (
  <li style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '2px 8px', padding: '5px 0', borderBottom: '1px dashed #eadfcd' }}>
    <b style={{ fontSize: '0.95rem', color: tone }}>{hi}</b>
    <span style={{ fontSize: '0.85rem', color: '#4a4034' }}>{en}</span>
    {note && <span style={{ ...tag('#fdf3dc', '#8a5a00'), fontSize: '0.66rem' }}>{note}</span>}
  </li>
);

// Everything the customer told us about one piece, plainly.
function CustomerSaid({ it }) {
  const reasons = reasonsOf(it);
  const fit = reasons.includes('fit');
  const issues = issuesFor(it.garment);
  const lines = [];
  (hasDamage(it) ? it.damage || [] : []).forEach((id) => {
    const d = damageWords(id);
    lines.push(<Said key={`d-${id}`} hi={d.hi} en={d.en} tone="#9b2c2c" />);
  });
  if (fit && it.mode === 'call' && it.call) {
    lines.push(<Said key="call" hi={`${it.call.kind === 'video' ? 'Video' : 'Phone'} call booked`} en={`${pickupText({ day: it.call.day })} · ${slotLabel(it.call.slot)} · on the Call Schedule; note the changes there`} tone="#1d4f91" />);
  }
  if (fit && it.mode !== 'call') {
    (it.changes || []).forEach((c, i) => {
      const w = changeWords(c, issues);
      lines.push(<Said key={`c-${i}`} hi={w.hi} en={`${w.en} (${changeText(c)})`} note={c.unsure ? 'not sure how much' : ''} />);
    });
  }
  const nothing = !lines.length && !it.note && !it.voiceNote;
  return (
    <div style={{ background: '#fbf7ef', border: '1px solid #eadfcd', borderRadius: 4, padding: '10px 12px', marginTop: 8 }}>
      <div style={label}>ग्राहक ने कहा · Customer said</div>
      {lines.length > 0 && <ul style={{ listStyle: 'none', margin: '4px 0 0', padding: 0 }}>{lines}</ul>}
      {it.voiceNote && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ ...tag('#000', '#ddb56c') }}>VOICE NOTE</span>
          <audio controls preload="none" src={it.voiceNote} style={{ height: 34, maxWidth: '100%' }} />
        </div>
      )}
      {it.note && <blockquote style={{ margin: '8px 0 0', padding: '6px 10px', borderLeft: '3px solid #9d7130', background: '#fff', fontSize: '0.95rem' }}>“{it.note}”</blockquote>}
      {nothing && <div style={{ ...hint, marginTop: 4 }}>Only photos. Look at the marks, or call them.</div>}
    </div>
  );
}

function ItemBlock({ it, r, rows, closed, finals, setFinals, fix, setFix }) {
  const reasons = reasonsOf(it);
  const fit = reasons.includes('fit');
  const damage = hasDamage(it);
  const used = usedFor(rows, it.orderDocId, it.garment);
  const late = damage && it.receivedAt && toDate(r.createdAt) - toDate(it.receivedAt) > DAMAGE_WINDOW_HOURS * 3600000;
  return (
    <div style={{ borderTop: '1px dashed #e2d7c6', paddingTop: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: '1.05rem' }}>{it.garment}</strong>
        <span style={{ fontSize: '0.85rem' }}>{it.productName}{it.clientName ? ` · for ${it.clientName}` : ''}</span>
        {fit && <span style={tag('#1d4f91')}>FIT</span>}
        {damage && <span style={tag('#9b2c2c')}>DAMAGE</span>}
        {fit && <span style={hint}>{used} of {FREE_ALTERATIONS} free alterations used (with this one)</span>}
        {damage && !fit && <span style={hint}>Damage: not counted in the {FREE_ALTERATIONS} free</span>}
      </div>
      {it.receivedAt && (
        <div style={{ ...hint, marginTop: 2, color: late ? '#9b2c2c' : hint.color }}>
          Received {dayOf(it.receivedAt)} · asked {gap(it.receivedAt, r.createdAt)} later
          {late ? ` · after the ${DAMAGE_WINDOW_HOURS}-hour damage window` : ` · windows: damage ${DAMAGE_WINDOW_HOURS} h, fit ${FIT_WINDOW_DAYS} days`}
        </div>
      )}
      <CustomerSaid it={it} />
      {(it.marks || []).length > 0 && <span style={{ ...hint, display: 'block', marginTop: 8 }}>Red numbers: the {it.marks.length} {it.marks.length === 1 ? 'place' : 'places'} the customer marked as wrong.</span>}
      <Photos urls={it.photos} marks={it.marks || []} big />

      {damage && !closed && (
        <div style={{ marginTop: 10 }}>
          <div style={label}>How we put it right (the customer sees this)</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {Object.entries(DAMAGE_FIXES).map(([k, f]) => (
              <button key={k} type="button" style={btn(fix === k, k === 'refund' ? '#9b2c2c' : undefined)} onClick={() => setFix(k)}>{f.en}</button>
            ))}
          </div>
        </div>
      )}
      {damage && closed && DAMAGE_FIXES[it.fix] && <div style={{ fontSize: '0.85rem', color: '#187044', marginTop: 6 }}>{DAMAGE_FIXES[it.fix].en}</div>}

      {fit && !closed && (
        <div style={{ marginTop: 10 }}>
          <div style={label}>Change actually made (fill before marking delivered back)</div>
          <FinalChanges item={it} value={finals} onChange={setFinals} />
        </div>
      )}
      {closed && (it.finalChanges || []).length > 0 && <div style={{ fontSize: '0.85rem', color: '#187044', marginTop: 4 }}>Done: {it.finalChanges.map(changeText).join(' · ')}</div>}
    </div>
  );
}

function RequestCard({ r, rows, onSave, onCredit, onRefund }) {
  const [reply, setReply] = useState(r.reply || '');
  const [finals, setFinals] = useState(() => (r.items || []).map((it) => it.finalChanges || (reasonsOf(it).includes('fit') ? it.changes : []) || []));
  const [fixes, setFixes] = useState(() => (r.items || []).map((it) => it.fix || (hasDamage(it) ? 'repair' : '')));
  const [rupees, setRupees] = useState(String(suggestedPayout(r.items, r.bill?.amount)));
  const [upiRef, setUpiRef] = useState('');
  const [refund, setRefund] = useState({ amount: '', ref: '' });
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState('');
  const route = ROUTES[r.route] || ROUTES.pickup;
  const next = (NEXT[r.route] || {})[r.status];
  const closed = isClosed(r.status);
  const damage = (r.items || []).some(hasDamage);

  const run = async (name, fn) => { setBusy(name); try { await fn(); } finally { setBusy(''); } };
  const items = () => (r.items || []).map((it, i) => ({
    ...it,
    finalChanges: (finals[i] || []).filter((c) => c.area && c.direction && Number(c.inches) > 0),
    ...(hasDamage(it) ? { fix: fixes[i] || 'repair' } : {}),
  }));
  const tell = notify && !!r.email;
  const move = (status) => run(status, () => onSave(r, { status, items: items(), reply: reply.trim(), ...(status === 'done' && r.route === 'pickup' ? { returnedAt: new Date() } : {}) }, tell));
  const decline = () => {
    if (!reply.trim()) { window.alert('Write a message to the customer first: why it cannot be done, and what we will do instead.'); return; }
    return run('declined', () => onSave(r, { status: 'declined', reply: reply.trim() }, tell));
  };
  const cap = allowanceCap(r.items);
  const upiLink = r.upi ? `upi://pay?pa=${encodeURIComponent(r.upi)}&pn=${encodeURIComponent(r.name || 'Customer')}&am=${Math.round(Number(rupees) || 0)}&cu=INR&tn=${encodeURIComponent(`Maviinci alteration ${r.orderId || ''}`)}` : '';
  const credit = () => {
    const n = Math.round(Number(rupees) || 0);
    if (!(n > 0)) { window.alert('Enter the amount paid, in rupees.'); return; }
    if (n > cap && !window.confirm(`₹${n} is more than the limit of ₹${cap} for these pieces. Pay it anyway?`)) return;
    if (!upiRef.trim()) { window.alert('Pay by UPI first, then enter the UPI reference (UTR) of that payment.'); return; }
    if (!window.confirm(`Mark ₹${n} as paid to ${r.upi} (ref ${upiRef.trim()})?`)) return;
    return run('credit', () => onCredit(r, n, upiRef.trim(), items(), reply.trim(), tell));
  };
  const refundIt = () => {
    const n = Math.round(Number(refund.amount) || 0);
    if (!(n > 0)) { window.alert('Enter the amount refunded, in rupees.'); return; }
    if (!refund.ref.trim()) { window.alert('Refund first, then enter its reference.'); return; }
    if (!window.confirm(`Mark ₹${n} as refunded (ref ${refund.ref.trim()})? This closes the request.`)) return;
    return run('refund', () => onRefund(r, n, refund.ref.trim(), items(), reply.trim(), tell));
  };
  // The WhatsApp message for where it is now (and what the admin typed).
  const wa = r.phone ? `https://wa.me/91${phone10(r.phone)}?text=${encodeURIComponent(`Namaste ${r.name || ''},\n${updateMessage({ ...r, reply: reply.trim(), items: items() }).text}\n— MAVIINCI`)}` : '';
  const canRefund = damage && fixes.includes('refund') && ['approved', 'picked_up', 'altering'].includes(r.status);

  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 12, opacity: closed ? 0.85 : 1, borderLeft: damage ? '4px solid #9b2c2c' : card.border }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: '1rem' }}>{r.name || r.account}</strong>
        <span style={pill(r.status)}>{statusText(r)}</span>
        <span style={tag('#000', '#ddb56c')}>{route.label}</span>
        {damage && <span style={tag('#9b2c2c')}>DAMAGE</span>}
        <span style={hint}>Order {r.orderId}</span>
        <span style={{ ...hint, marginLeft: 'auto' }}>{when(r.createdAt)}{!closed ? ` · ${age(r.createdAt)}` : ''}</span>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.85rem', alignItems: 'center' }}>
        {r.phone && <span>{r.phone}</span>}
        {r.email && <span>{r.email}</span>}
        {(r.items || [])[0]?.orderDocId && <button type="button" style={{ ...btn(false), height: 28 }} onClick={() => { if (!openOrderOnWebsite(r.items[0].orderDocId)) alert('Allow pop-ups for this site.'); }}>Open the order</button>}
      </div>
      {r.route === 'pickup' && (r.pickupAddress || r.pickup) && (
        <div style={{ fontSize: '0.88rem', background: '#f6f0e6', padding: '8px 10px', borderRadius: 3 }}>
          {r.pickup && <div><b>Pick up:</b> {pickupText(r.pickup)}</div>}
          {r.pickupAddress && <div><b>From:</b> {addressText(r.pickupAddress)}</div>}
        </div>
      )}

      {(r.items || []).map((it, i) => (
        <ItemBlock key={i} it={it} r={r} rows={rows} closed={closed}
          finals={finals[i]} setFinals={(v) => setFinals((f) => f.map((x, j) => (j === i ? v : x)))}
          fix={fixes[i]} setFix={(v) => setFixes((f) => f.map((x, j) => (j === i ? v : x)))} />
      ))}

      {r.route === 'local' && r.bill && (
        <div style={{ background: '#f6f0e6', padding: 10, borderRadius: 3 }}>
          <b style={{ fontSize: '0.85rem' }}>Bill from their tailor{r.bill.amount ? ` · ₹${r.bill.amount}` : ''}</b>
          <div style={{ fontSize: '0.8rem', marginTop: 4 }}>Limit for these pieces: ₹{cap} ({(r.items || []).map((it) => `${it.garment} ₹${allowanceFor(it.garment)}`).join(', ')})</div>
          {r.upi && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6, fontSize: '0.85rem' }}>
              <span>UPI: <b>{r.upi}</b></span>
              <button type="button" style={{ ...btn(false), height: 28 }} onClick={() => navigator.clipboard && navigator.clipboard.writeText(r.upi)}>Copy</button>
              <a href={upiLink} style={{ ...btn(false), height: 28, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>Open in UPI app (phone)</a>
            </div>
          )}
          <Photos urls={r.bill.photos} />
          <span style={{ ...hint, display: 'block', marginTop: 6 }}>After the alteration:</span>
          <Photos urls={r.afterPhotos} />
        </div>
      )}
      {r.status === 'credited' && r.paid && <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#187044' }}>Paid ₹{r.paid.amount} to {r.paid.upi} · UPI ref {r.paid.ref}{r.paid.by ? ` · by ${r.paid.by}` : ''}</div>}
      {r.status === 'refunded' && r.refund && <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#187044' }}>Refunded ₹{r.refund.amount} · ref {r.refund.ref}{r.refund.by ? ` · by ${r.refund.by}` : ''}</div>}

      {!closed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px dashed #e2d7c6', paddingTop: 10 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem', fontWeight: 700, color: '#4a4034' }}>
            Message to the customer (shown on their order, and in the email and WhatsApp)
            <input style={input} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="e.g. Courier comes tomorrow 11–2. Keep it packed." />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}>
            <input type="checkbox" checked={notify && !!r.email} disabled={!r.email} onChange={(e) => setNotify(e.target.checked)} />
            {r.email ? `Email the customer at ${r.email} when the step changes` : 'No email on this account: send the WhatsApp message instead'}
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {next && <button type="button" style={btn(true, '#187044')} disabled={!!busy} onClick={() => move(next)}>{busy === next ? 'Saving…' : r.route === 'local' && next === 'approved' ? 'Approve: go ahead with their tailor' : NEXT_LABEL[next]}</button>}
            {r.route === 'local' && r.status === 'bill_sent' && (
              <>
                <span style={{ fontSize: '0.8rem', fontWeight: 700 }}>Paid ₹</span>
                <input style={{ ...input, width: 90 }} type="number" min="0" value={rupees} onChange={(e) => setRupees(e.target.value)} aria-label="Amount paid" />
                <input style={{ ...input, width: 170 }} value={upiRef} onChange={(e) => setUpiRef(e.target.value)} placeholder="UPI ref (UTR)" aria-label="UPI reference" />
                <button type="button" style={btn(true, '#187044')} disabled={!!busy} onClick={credit}>{busy === 'credit' ? 'Saving…' : `Mark ₹${Math.round(Number(rupees) || 0)} paid`}</button>
              </>
            )}
            <button type="button" style={btn(false)} disabled={!!busy} onClick={() => run('reply', () => onSave(r, { reply: reply.trim(), items: items() }, false))}>{busy === 'reply' ? 'Saving…' : 'Save'}</button>
            {wa && <a href={wa} target="_blank" rel="noopener noreferrer" style={{ ...btn(false, '#187044'), display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>WhatsApp the update</a>}
            <button type="button" style={btn(false, '#9b2c2c')} disabled={!!busy} onClick={decline}>Turn down</button>
          </div>
          {canRefund && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', background: '#fdecea', padding: 8, borderRadius: 3 }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#9b2c2c' }}>Refund ₹</span>
              <input style={{ ...input, width: 90 }} type="number" min="0" value={refund.amount} onChange={(e) => setRefund((x) => ({ ...x, amount: e.target.value }))} aria-label="Refund amount" />
              <input style={{ ...input, width: 190 }} value={refund.ref} onChange={(e) => setRefund((x) => ({ ...x, ref: e.target.value }))} placeholder="Refund reference" aria-label="Refund reference" />
              <button type="button" style={btn(true, '#9b2c2c')} disabled={!!busy} onClick={refundIt}>{busy === 'refund' ? 'Saving…' : 'Mark refunded'}</button>
            </div>
          )}
        </div>
      )}
      {r.lastEmail && <div style={hint}>Emailed the customer: {statusText(r, r.lastEmail.status)} · {when(r.lastEmail.at)}</div>}
      {(r.history || []).length > 0 && (
        <div style={hint}>{r.history.map((h) => `${statusText(r, h.status)} ${when(h.at)}${h.by && h.by !== 'customer' ? ` (${h.by})` : ''}`).join('  ·  ')}</div>
      )}
    </div>
  );
}

// Emails go through sendReplyEmail, which needs a notification to hang the
// branded email on: one per request, kept read so it does not ring the bell.
const emailCustomer = async (r, status) => {
  const found = await db.collection('Notifications').where('details.alterationId', '==', r.id).where('type', '==', 'alteration_update').limit(1).get();
  let id = found.empty ? '' : found.docs[0].id;
  if (!id) {
    const ref = await db.collection('Notifications').add({
      type: 'alteration_update', title: 'Alteration update emailed', message: `${r.name || r.account} · order ${r.orderId || ''}`,
      link: '/admin/30', read: true, timestamp: new Date(),
      details: { alterationId: r.id, name: r.name || '', phone: r.phone || '', orderNo: r.orderId || '' },
    });
    id = ref.id;
  }
  const { subject, text } = updateMessage(r, status);
  await functions.httpsCallable('sendReplyEmail')({ notificationId: id, to: r.email, subject, message: text });
};

function CarePage() {
  const { user } = useContext(AuthContext) || {};
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => db.collection(ALTERATIONS_COLLECTION).onSnapshot(
    (snap) => { const out = []; snap.forEach((d) => out.push({ id: d.id, ...d.data() })); setRows(out); },
    (e) => { console.error('alterations', e); setRows([]); setMessage('Could not read alteration requests. Check that the Firestore rules allow the Alterations collection.'); },
  ), []);

  const list = useMemo(() => (rows || []).slice().sort((a, b) => (toDate(b.createdAt) || 0) - (toDate(a.createdAt) || 0)), [rows]);
  const q = search.trim().toLowerCase();
  const match = (r, f) => (f === 'all' ? true : f === 'open' ? !isClosed(r.status) : f === 'done' ? ['done', 'credited', 'refunded'].includes(r.status)
    : f === 'damage' ? !isClosed(r.status) && (r.items || []).some(hasDamage) : r.status === f);
  const shown = list.filter((r) => match(r, filter)
    && (!q || [r.name, r.account, r.phone, r.orderId, ...(r.items || []).map((it) => `${it.garment} ${it.productName} ${it.clientName}`)].some((v) => String(v || '').toLowerCase().includes(q))));
  const count = (f) => list.filter((r) => match(r, f)).length;

  const by = user?.email || 'admin';
  // Write the step, then tell the customer by email when asked.
  const write = async (r, patch, tell, done) => {
    const now = new Date();
    const history = patch.status && patch.status !== r.status ? [...(r.history || []), { status: patch.status, at: now, by }] : (r.history || []);
    try {
      await db.collection(ALTERATIONS_COLLECTION).doc(r.id).set({ ...patch, history, updatedAt: now }, { merge: true });
    } catch (e) {
      console.error('alteration save', e);
      setMessage(`Could not save. (${e?.message || e})`);
      return;
    }
    let mail = '';
    if (tell && patch.status && r.email) {
      try {
        await emailCustomer({ ...r, ...patch }, patch.status);
        await db.collection(ALTERATIONS_COLLECTION).doc(r.id).set({ lastEmail: { status: patch.status, at: new Date() } }, { merge: true });
        mail = ' Emailed the customer.';
      } catch (e) {
        console.error('alteration email', e);
        mail = ' The email did not go: send the WhatsApp message.';
      }
    }
    setMessage(`${r.name || r.account}: ${done}.${mail}`);
  };
  const save = (r, patch, tell) => write(r, patch, tell, patch.status ? statusText({ ...r, ...patch }, patch.status) : 'saved');
  // The money goes by UPI from our own app; this records what was paid and
  // the payment's reference, which the customer then sees.
  const credit = (r, rupees, ref, items, reply, tell) => write(r, {
    status: 'credited', paid: { amount: rupees, ref, upi: r.upi || '', at: new Date(), by }, items, reply,
  }, tell, `₹${rupees} paid to ${r.upi || 'their UPI'}`);
  const refund = (r, rupees, ref, items, reply, tell) => write(r, {
    status: 'refunded', refund: { amount: rupees, ref, at: new Date(), by }, items, reply,
  }, tell, `₹${rupees} refunded`);

  if (rows === null) return <div style={{ padding: 20 }}>Loading alteration requests…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '4px 0 40px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 }}>
        {[['New', count('requested'), '#8a5a00'], ['Damage open', count('damage'), '#9b2c2c'], ['Bill to check', count('bill_sent'), '#8a5a00'], ['Open', count('open'), '#1d4f91'], ['Done', count('done'), '#187044']].map(([t, v, color]) => (
          <div key={t} style={{ ...card, padding: '11px 12px' }}>
            <div style={{ fontSize: '1.2rem', fontWeight: 900, color }}>{v}</div>
            <div style={{ marginTop: 4, fontSize: '0.64rem', color: '#6A7079', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t}</div>
          </div>
        ))}
      </div>
      <div style={{ ...card, background: '#f6f0e6', borderColor: '#d7cbb7', fontSize: '0.85rem', color: '#4a4034' }}>
        Each piece gets {FREE_ALTERATIONS} free alterations within {FIT_WINDOW_DAYS} days of delivery. Dirty, damaged or wrong pieces are reported within {DAMAGE_WINDOW_HOURS} hours: our fault, so they are not counted and always come by pickup; choose how we put it right (clean, repair, remake or refund). When a piece comes back from us, both windows start again. Pickup: approve, then move it along as it travels. Own tailor: approve, wait for the bill and UPI ID, pay the bill by UPI up to the limit (coat ₹500, jacket ₹300, pant / shirt / kurta / pajama / vest ₹200), then record the amount and the UPI reference. Each step can email the customer; WhatsApp the update when there is no email. Write down the change actually made before you finish; it is kept for the customer's next order.
      </div>
      {message && <div style={{ ...card, background: '#f6f0e6', borderColor: '#9d7130', fontWeight: 600, fontSize: '0.88rem' }}>{message}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {FILTERS.map(([k, t]) => <button key={k} type="button" onClick={() => setFilter(k)} style={{ ...btn(filter === k), height: 32 }}>{t}{k !== 'all' && count(k) ? ` (${count(k)})` : ''}</button>)}
        </div>
        <input style={{ ...input, width: 260 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, order, piece" />
      </div>
      {shown.length === 0 && <div style={{ ...card, color: '#7a6750' }}>{list.length ? 'Nothing here.' : 'No alteration requests yet. Customers ask from their order page after delivery.'}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {shown.map((r) => <RequestCard key={`${r.id}-${r.status}`} r={r} rows={list} onSave={save} onCredit={credit} onRefund={refund} />)}
      </div>
    </div>
  );
}

export default CarePage;
